/**
 * Pure math for the Pre-Auth Hold screen's credit breakdown.
 *
 * Inputs (both in cents):
 *   selectedBudgetCents — what the customer chose (preset or custom).
 *   codeAmountCents     — the raw amount on the redeem code (may
 *                         exceed the selected budget; unused value
 *                         does not carry forward).
 *
 * Outputs (all in cents):
 *   creditAppliedCents  — min(codeAmountCents, selectedBudgetCents).
 *                         What the "Charging Credit" row shows.
 *   cardAuthCents       — max(0, selectedBudgetCents - codeAmountCents).
 *                         What Fiserv is asked to authorize.
 *   fullyCovered        — true when a code is applied AND
 *                         cardAuthCents === 0 AND a budget is set.
 *   hasCarryForward     — true when the code amount > selected budget
 *                         (i.e. some code value is not spendable now).
 *   creditWithoutBudget — true when a code is applied but no budget
 *                         has been picked yet. Continue must be blocked.
 *
 * Extracted so the rules are unit-testable without booting React.
 */
export function computeCreditBreakdown({
  selectedBudgetCents = 0,
  codeAmountCents = 0,
} = {}) {
  const budget = Math.max(0, Number(selectedBudgetCents) || 0);
  const code   = Math.max(0, Number(codeAmountCents)     || 0);

  const creditAppliedCents = Math.min(code, budget);
  const cardAuthCents      = Math.max(0, budget - code);
  const fullyCovered       = code > 0 && cardAuthCents === 0 && budget > 0;
  const hasCarryForward    = code > 0 && budget > 0 && code > budget;
  const creditWithoutBudget = code > 0 && budget === 0;

  return {
    creditAppliedCents,
    cardAuthCents,
    fullyCovered,
    hasCarryForward,
    creditWithoutBudget,
  };
}
